import { AuthorisedUser, Collateral, CollateralBasket, Organisation, SpreadsheetMappingType, TradingEntity } from 'api'
import { AxeActions } from 'api/AxeAction'
import { Locale, Translation } from 'locale'
import { noop } from 'lodash'
import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { createCollateralBasketByIsinCusipOrIdGetterSelector } from 'selectors/collateralBasketSelectors'
import { createCollateralByIsinOrIdGetterSelector } from 'selectors/collateralSelectors'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { organisationsByShortCodesGetterSelector } from 'selectors/organisationSelectors'
import { tradingEntityByShortNameAndOrgIdGetterSelector } from 'selectors/tradingEntitySelectors'
import { NotificationActions, NotificationType } from 'store/notification'
import { SpreadsheetParseCompleteHandler, SpreadsheetParseDataHandler, SpreadsheetParseDialog } from '../upload/SpreadsheetParseDialog'
import { createMapAxeDataToAxeEventCreate, TradingEntityNotFoundError } from './mapAxeSpreadsheet'
import { AxeParseResult, parseAxeSpreadsheetFactory } from './parseAxeSpreadsheet'

interface AxeUploadDialogProps {
  initialFile?: File
  initialClipboard?: string
  onClose?: () => unknown
  onShowGrowl: typeof NotificationActions.showGrowl
  createAxeBulk: typeof AxeActions.createBulk
  getCollateralBasketByIsinCusipOrId: (id: string) => CollateralBasket | null
  getCollateralByIsinOrId: (id: string) => Collateral | null
  getTradingEntityByShortNameAndOrganisationId: (organisationId: string, tradingEntityShortName: string) => TradingEntity | null
  getOrganisationsByShortNames: (shortNames: string[]) => Organisation[] | null
  authorisedUser: AuthorisedUser
}

export const AxeUploadDialogComponent: React.FC<AxeUploadDialogProps> = props => {
  const {
    initialFile,
    initialClipboard,
    onClose = noop,
    onShowGrowl,
    createAxeBulk,
    getCollateralBasketByIsinCusipOrId,
    getCollateralByIsinOrId,
    getTradingEntityByShortNameAndOrganisationId,
    getOrganisationsByShortNames,
    authorisedUser
  } = props

  const axeParser = parseAxeSpreadsheetFactory(authorisedUser)
  const mapAxeDataToAxeEventCreate = createMapAxeDataToAxeEventCreate(
    getCollateralBasketByIsinCusipOrId,
    getCollateralByIsinOrId,
    getTradingEntityByShortNameAndOrganisationId,
    getOrganisationsByShortNames,
    authorisedUser
  )

  const handleParseData: SpreadsheetParseDataHandler<AxeParseResult> = async (rowData, _filename, spreadsheetMapping) =>
    axeParser({ rowData, filename: _filename, spreadsheetMapping })

  const handleComplete: SpreadsheetParseCompleteHandler<AxeParseResult> = async ({ parseResult }) => {
    try {
      const childEvents = parseResult.pendingAxes.map(mapAxeDataToAxeEventCreate)
      createAxeBulk({
        childEvents
      })
    } catch (e) {
      if (e instanceof TradingEntityNotFoundError) {
        onShowGrowl({ type: NotificationType.Error, messageText: e.message })
      }
    } finally {
      onClose()
    }
  }

  return (
    <SpreadsheetParseDialog<AxeParseResult>
      title={Locale.translate(Translation.TC_ADD_AXES)}
      spreadsheetMappingType={SpreadsheetMappingType.AXE}
      parseResultItemsGetter={parseResult => parseResult.pendingAxes}
      translations={{
        emptyDropzoneNarrative: Translation.SC_UPLOAD_DROPZONE_NARRATIVE,
        parseSuccessMessage: Translation.SC_AXE_SPREADSHEET_SUCCESS,
        parseErrorMessage: Translation.SC_AXE_SPREADSHEET_ERROR
      }}
      onComplete={handleComplete}
      onParseData={handleParseData}
      onClose={onClose}
      clipboard={initialClipboard}
      uploadFile={initialFile}
    />
  )
}

export const AxeUploadDialog = connect(
  createStructuredSelector({
    getCollateralBasketByIsinCusipOrId: createCollateralBasketByIsinCusipOrIdGetterSelector,
    getCollateralByIsinOrId: createCollateralByIsinOrIdGetterSelector,
    getTradingEntityByShortNameAndOrganisationId: tradingEntityByShortNameAndOrgIdGetterSelector,
    getOrganisationsByShortNames: organisationsByShortCodesGetterSelector,
    authorisedUser: createAuthorisedUserSelector()
  }),
  {
    onShowGrowl: NotificationActions.showGrowl,
    createAxeBulk: AxeActions.createBulk
  }
)(AxeUploadDialogComponent)
